import { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../modules/auth/context/AuthContext';
import { Formik, Form } from 'formik';
import ScheduleTab from '../components/ScheduleTab';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roomService } from '../../../shared/services/rooms/roomsServices';
import { RoomFormHeader } from '../components/RoomForm/RoomFormHeader';
import { RoomFormTabs } from '../components/RoomForm/RoomFormTabs';
import { RoomFormFields } from '../components/RoomForm/RoomFormFields';
import { RoomFormActions } from '../components/RoomForm/RoomFormActions';
import { roomFormValidationSchema } from '../validation/RoomFormValidation';

interface Schedule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  interval_minutes: number;
  is_active: boolean;
}

interface RoomData {
  id: string;
  name: string;
  description: string;
  tenant_id: string;
  schedules: Schedule[];
}

export default function RoomFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: authUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [tenantId, setTenantId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  
  const isAdmin = authUser?.attributes?.find((a: any) => a.key === 'type_flag')?.value === 'admin';
  const mode = id ? 'edit' : 'create';

  const { data: roomData, isLoading: isDataLoading } = useQuery<any>({
    queryKey: ['room', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await roomService.getById(id);
      return response.data;
    },
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => roomService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      navigate('/rooms');
    },
    onError: (error: any) => {
      setError(
        typeof error === 'string'
          ? error
          : error?.response?.data?.message || error?.message || 'Erro ao criar sala'
      );
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => roomService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      queryClient.invalidateQueries({ queryKey: ['room', id] });
      navigate('/rooms');
    },
    onError: (error: any) => {
      setError(
        typeof error === 'string'
          ? error
          : error?.response?.data?.message || error?.message || 'Erro ao atualizar sala'
      );
    }
  });

  useEffect(() => {
    if (roomData) {
      setTenantId(roomData.tenant_id);
    } else {
      setTenantId(authUser?.tenant_id || '');
    }
  }, [roomData, authUser?.tenant_id]);

  const getInitialSchedules = (): Schedule[] => {
    if (roomData?.schedules && roomData.schedules.length > 0) {
      return roomData.schedules.map((s: any) => ({
        id: s.id,
        day_of_week: s.day_of_week,
        start_time: s.start_time.substring(0, 5),
        end_time: s.end_time.substring(0, 5),
        interval_minutes: s.interval_minutes,
        is_active: s.is_active
      }));
    }
    
    return Array.from({ length: 7 }, (_, index) => ({
      day_of_week: index,
      start_time: '06:00',
      end_time: '23:00',
      interval_minutes: 60,
      is_active: true
    }));
  };

  const [schedules, setSchedules] = useState<Schedule[]>(getInitialSchedules());

  const initialValues = {
    name: roomData?.name || '',
    description: roomData?.description || '',
    virtualKey: roomData?.virtualKey || false,
  };

  const handleBack = () => {
    navigate('/rooms');
  };

  const handleTabChange = (_: any, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseError = () => {
    setError(null);
  };

  if (isDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {typeof error === 'string' ? error : (error && typeof error === 'object' && 'message' in error) ? (error as any).message : 'Erro desconhecido'}
        </Alert>
      </Snackbar>

      <Formik
        initialValues={initialValues}
        validationSchema={roomFormValidationSchema}
        enableReinitialize
        onSubmit={async (values) => {
          const payload = {
            name: values.name,
            description: values.description,
            virtualKey: values.virtualKey,
            tenant_id: isAdmin ? tenantId : authUser?.tenant_id,
            schedules: schedules.map(schedule => ({
              day_of_week: schedule.day_of_week,
              start_time: schedule.start_time + ':00',
              end_time: schedule.end_time + ':00',
              interval_minutes: schedule.interval_minutes,
              is_active: schedule.is_active
            }))
          };

          if (mode === 'create') {
            createMutation.mutate(payload);
          } else {
            updateMutation.mutate(payload);
          }
        }}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            <Box sx={{ 
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 3, 
              p: 4, 
              maxWidth: 800, 
              margin: '32px auto', 
              boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)' 
            }}>
              <RoomFormHeader mode={mode} />
              <RoomFormTabs value={tabValue} onChange={handleTabChange} />
              <Box sx={{ display: tabValue === 0 ? 'block' : 'none' }}>
                <RoomFormFields 
                  values={values}
                  errors={errors}
                  touched={touched}
                  isAdmin={isAdmin}
                  isSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                />
              </Box>
              <Box sx={{ display: tabValue === 1 ? 'block' : 'none' }}>
                <ScheduleTab 
                  schedules={schedules}
                  setSchedules={setSchedules}
                />
              </Box>
              <RoomFormActions 
                isSubmitting={isSubmitting}
                onBack={handleBack}
                mode={mode}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}